import type { SubscriberMap } from './interfaces.js';
import type { RHMGlobal } from '@/typescript/interfaces/rhm-interfaces.js';

/**
 * @private
 * @type {SubscriberMap}
 * @description Stores all event subscribers organized by event name
 */
const subscribers: SubscriberMap = {};

/**
 * @function subscribe
 * @description Subscribes a callback function to a specific event
 * @param {string} eventName - The name of the event to subscribe to
 * @param {(data: any) => void} callback - The function to call when the event is published
 * @returns {() => void} An unsubscribe function to remove the callback from the event
 * @example
 * const unsubscribe = subscribe('user-login', (userData) => {
 *   console.log('User logged in:', userData);
 * });
 *
 * // Later, to unsubscribe:
 * unsubscribe();
 */
function subscribe(eventName: string, callback: (data: any) => void): () => void {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe(): void {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

/**
 * @function publish
 * @description Publishes an event with data to all subscribed callbacks
 * @param {string} eventName - The name of the event to publish
 * @param {any} data - The data to pass to all subscribed callbacks
 * @example
 * publish('user-login', { userId: 123, username: 'john_doe' });
 */
function publish(eventName: string, data: any): void {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

/**
 * @function getSubscriberCount
 * @description Gets the number of subscribers for a specific event
 * @param {string} eventName - The name of the event
 * @returns {number} The number of subscribers for the event
 */
function getSubscriberCount(eventName: string): number {
  return subscribers[eventName] ? subscribers[eventName].length : 0;
}

/**
 * @function clearEvent
 * @description Removes all subscribers for a specific event
 * @param {string} eventName - The name of the event to clear
 */
function clearEvent(eventName: string): void {
  if (subscribers[eventName]) {
    delete subscribers[eventName];
  }
}

/**
 * @function clearAllEvents
 * @description Removes all subscribers for all events
 */
function clearAllEvents(): void {
  Object.keys(subscribers).forEach(key => {
    delete subscribers[key];
  });
}

// Initialize the global rhm object if it doesn't exist
if (typeof window.rhm === 'undefined') {
  window.rhm = {} as RHMGlobal;
}

// Export the functions to the global rhm object
window.rhm.subscribe = subscribe;
window.rhm.publish = publish;

// Export for module usage
export default {
  subscribe,
  publish,
  getSubscriberCount,
  clearEvent,
  clearAllEvents
};
